/**
 * Работа с датами.
 *
 * Все даты в системе — строки ISO `YYYY-MM-DD` без времени и без часового пояса.
 * Это сознательно: дата получения и дата сдачи заказа — календарные факты
 * лаборатории, а не моменты времени. `new Date("2026-08-16")` разбирается как
 * полночь UTC, и в Иркутске (UTC+8) наивное сравнение съезжает на день.
 */
/** Сегодняшняя дата в местном часовом поясе как ISO-строка. */
export function todayIso(now = new Date()) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Разбор ISO-даты в календарные части без участия часовых поясов. */
export function parseIso(date) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match)
        return null;
    return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}
/** Порядковый номер дня — для разницы дат без часовых поясов. */
function toDayNumber(date) {
    const parts = parseIso(date);
    if (!parts)
        return null;
    return Math.floor(Date.UTC(parts.y, parts.m - 1, parts.d) / 86_400_000);
}
/**
 * Сколько дней осталось до даты. Отрицательное значение — дата уже прошла.
 * Обе даты приводятся к календарным дням, поэтому «сегодня» всегда даёт 0.
 */
export function daysUntil(date, today) {
    if (!date)
        return Number.POSITIVE_INFINITY;
    const target = toDayNumber(date);
    const base = toDayNumber(today);
    if (target === null || base === null)
        return Number.POSITIVE_INFINITY;
    return target - base;
}
/** Формат даты для интерфейса и документов: `16-08-2026`. */
export function formatDate(date) {
    const parts = date ? parseIso(date) : null;
    if (!parts)
        return '—';
    return `${String(parts.d).padStart(2, '0')}-${String(parts.m).padStart(2, '0')}-${parts.y}`;
}
/** Месяцы в родительном падеже: «06 августа 2026», а не «06 август 2026». */
const MONTHS_GENITIVE = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
/**
 * Дата словом для печатных документов: `06 августа 2026`.
 *
 * Именно так набраны заголовки счетов и актов лаборатории — сверено по 58
 * реальным файлам. Цифровой формат `06-08-2026` в документе выглядит чужим:
 * бухгалтерия клиник сверяет присланное с привычным.
 */
export function formatDateLong(date) {
    const parts = date ? parseIso(date) : null;
    if (!parts)
        return '—';
    const month = MONTHS_GENITIVE[parts.m - 1] ?? '';
    return `${String(parts.d).padStart(2, '0')} ${month} ${parts.y}`;
}
/**
 * Дата точками: `24.03.2025`.
 *
 * Так в актах указана дата договора — в отличие от даты самого документа,
 * которая пишется словом. Разные форматы в одном предложении не описка:
 * дата акта читается вслух, дата договора служит ссылкой на реквизит.
 */
export function formatDateDots(date) {
    const parts = date ? parseIso(date) : null;
    if (!parts)
        return '';
    return `${String(parts.d).padStart(2, '0')}.${String(parts.m).padStart(2, '0')}.${parts.y}`;
}
/** День месяца двумя цифрами — в заголовке акта он стоит в кавычках отдельно. */
export function dayOf(date) {
    const parts = date ? parseIso(date) : null;
    return parts ? String(parts.d).padStart(2, '0') : '';
}
/** Месяц и год словом: `августа 2026` — остаток заголовка акта после дня. */
export function monthYearGenitive(date) {
    const parts = date ? parseIso(date) : null;
    if (!parts)
        return '';
    return `${MONTHS_GENITIVE[parts.m - 1] ?? ''} ${parts.y}`;
}
/** Год из ISO-даты. Используется годовым счётчиком счетов (§2.2). */
export function yearOf(date) {
    return Number(date.slice(0, 4));
}
/** Месяц из ISO-даты в виде `YYYY-MM` — ключ группировки заказов по месяцам. */
export function monthKeyOf(date) {
    return date.slice(0, 7);
}
/** Название месяца по-русски: `август 2026`. */
export function formatMonthKey(monthKey) {
    const y = Number(monthKey.slice(0, 4));
    const m = Number(monthKey.slice(5, 7));
    if (!Number.isFinite(y) || !Number.isFinite(m))
        return monthKey;
    const names = [
        'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
        'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
    ];
    return `${names[m - 1] ?? monthKey} ${y}`;
}
/** Смещение месяца от текущего: `{ year, month }`, месяц 1..12. */
export function monthOffset(today, offset) {
    const parts = parseIso(today);
    if (!parts)
        return { year: 0, month: 0 };
    const zeroBased = parts.m - 1 + offset;
    return {
        year: parts.y + Math.floor(zeroBased / 12),
        month: ((zeroBased % 12) + 12) % 12 + 1,
    };
}
/** Ключ `YYYY-MM` для смещения месяца от текущего. */
export function monthKeyForOffset(today, offset) {
    const { year, month } = monthOffset(today, offset);
    return `${year}-${String(month).padStart(2, '0')}`;
}
//# sourceMappingURL=dates.js.map